import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PrinterIcon, ReceiptIcon, FilePdfIcon } from "@phosphor-icons/react";
import { exportNodeToPdf } from "@/lib/pdfExport";

const money = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function DailyClosing() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [openingCash, setOpeningCash] = useState("0");
  const [openingBank, setOpeningBank] = useState("0");
  const [data, setData] = useState(null);
  const [physicalCash, setPhysicalCash] = useState("");
  const [physicalOnline, setPhysicalOnline] = useState("");
  const printRef = useRef(null);

  const exportPdf = async () => {
    if (!printRef.current) return;
    try {
      await exportNodeToPdf(printRef.current, `daily-closing-${date}.pdf`, { orientation: "portrait" });
      toast.success("PDF downloaded (A4 portrait · 9:16)");
    } catch { toast.error("PDF export failed"); }
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/closing/daily?date=${date}&opening_cash=${openingCash || 0}&opening_bank=${openingBank || 0}`);
      setData(r.data);
    } catch { toast.error("Failed to load"); }
  }, [date, openingCash, openingBank]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-slate-400 overline" data-testid="loading-closing">Loading…</div>;

  const variance = physicalCash === "" ? null : (parseFloat(physicalCash) - data.closing.cash_in_hand).toFixed(2);
  const expectedOnline = (data.collections?.gpay || 0) + (data.collections?.wallet || 0);
  const onlineVariance = physicalOnline === "" ? null : (parseFloat(physicalOnline) - expectedOnline).toFixed(2);

  return (
    <div className="space-y-10" data-testid="daily-closing-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-b border-slate-200 pb-6 no-print">
        <div>
          <div className="overline text-slate-500">Day-end</div>
          <h1 className="font-display text-4xl font-black">Cash Tally Sheet</h1>
          <p className="text-slate-500 mt-2">Print-ready shift-end reconciliation for the shop counter.</p>
        </div>
        <div className="flex gap-3 items-end flex-wrap">
          <div><Label className="overline">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-none w-40" data-testid="closing-date" /></div>
          <div><Label className="overline">Opening Cash</Label>
            <Input type="number" step="0.01" value={openingCash} onChange={(e) => setOpeningCash(e.target.value)} className="rounded-none w-32 font-mono-num" data-testid="opening-cash-input" /></div>
          <div><Label className="overline">Opening Bank</Label>
            <Input type="number" step="0.01" value={openingBank} onChange={(e) => setOpeningBank(e.target.value)} className="rounded-none w-32 font-mono-num" data-testid="opening-bank-input" /></div>
          <Button variant="outline" onClick={exportPdf} className="rounded-none border-slate-900" data-testid="closing-export-pdf">
            <FilePdfIcon size={14} className="mr-2" /> PDF
          </Button>
          <Button variant="outline" onClick={() => window.print()} className="rounded-none border-slate-900" data-testid="print-button">
            <PrinterIcon size={14} className="mr-2" /> Print
          </Button>
        </div>
      </div>

      <div ref={printRef} className="print-area border border-slate-900 bg-white p-4 mx-auto" style={{ width: "260mm", aspectRatio: "16 / 9", maxWidth: "100%" }}>
        <div className="flex items-center justify-between border-b border-slate-900 pb-2 mb-3">
          <div>
            <div className="font-display font-black leading-tight" style={{ fontSize: 14 }}>Shree Sai Sayaji Indane</div>
            <div className="overline text-slate-500" style={{ fontSize: 9 }}>Daily Cash Tally Sheet</div>
          </div>
          <div className="text-right">
            <div className="overline text-slate-500" style={{ fontSize: 9 }}>Date</div>
            <div className="font-mono-num font-bold">{data.date}</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <Section title="Opening">
              <Row label="Cash-in-Hand" value={money(data.opening.cash)} />
            </Section>
            <Section title="Collections (In)">
              <Row label="Cash" value={money(data.collections.cash)} plus />
              <Row label="GPay" value={money(data.collections.gpay)} plus />
              <Row label="Wallet" value={money(data.collections.wallet)} plus />
              <Row label="Total" value={money(data.collections.total)} bold />
            </Section>
          </div>

          <div>
            <Section title="Expenses (Out)">
              <Row label="Cash Exp." value={money(data.expenses.cash)} minus />
              <Row label="Online Exp." value={money(data.expenses.online)} minus />
              <Row label="Total Spent" value={money(data.expenses.total)} bold />
            </Section>
          </div>

          <div>
            <div className="border-2 border-slate-900 p-2 space-y-1">
              <Row label="Closing Cash" value={money(data.closing.cash_in_hand)} bold big />
              <PhysicalRow
                label="Physical Cash in Hand"
                value={physicalCash}
                onChange={setPhysicalCash}
                testId="physical-cash-input"
              />
              {variance !== null && (
                <div className={`flex items-center justify-between py-0.5 border-b border-dashed ${parseFloat(variance) === 0 ? "border-emerald-600" : "border-[#FF2400]"}`}>
                  <div className="text-sm text-slate-500">Cash Variance</div>
                  <div
                    className={`font-mono-num font-bold text-sm ${parseFloat(variance) === 0 ? "text-emerald-600" : "text-[#FF2400]"}`}
                    data-testid="cash-variance"
                  >
                    {variance > 0 ? "+" : ""}₹{variance}
                  </div>
                </div>
              )}
              <Row label="Online Received (GPay)" value={money(expectedOnline)} bold big />
              <PhysicalRow
                label="Amount Received on GPay"
                value={physicalOnline}
                onChange={setPhysicalOnline}
                testId="physical-online-input"
                placeholder={String(expectedOnline || "")}
              />
              {onlineVariance !== null && (
                <div className={`flex items-center justify-between py-0.5 border-b border-dashed ${parseFloat(onlineVariance) === 0 ? "border-emerald-600" : "border-[#FF2400]"}`}>
                  <div className="text-sm text-slate-500">Online Variance</div>
                  <div
                    className={`font-mono-num font-bold text-sm ${parseFloat(onlineVariance) === 0 ? "text-emerald-600" : "text-[#FF2400]"}`}
                    data-testid="online-variance"
                  >
                    {onlineVariance > 0 ? "+" : ""}₹{onlineVariance}
                  </div>
                </div>
              )}
              <div className="pt-2 border-t border-slate-400 mt-2">
                <Row label="Grand Total" value={money(data.closing.grand_total)} bold big accent />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <div className="border-b border-slate-400 h-6"></div>
                <div className="overline text-slate-500 mt-1 text-center" style={{ fontSize: 8 }}>Cashier</div>
              </div>
              <div>
                <div className="border-b border-slate-400 h-6"></div>
                <div className="overline text-slate-500 mt-1 text-center" style={{ fontSize: 8 }}>Owner</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto no-print" data-testid="physical-count-panel">
        <div>
          <Label className="overline">Physical Cash in Hand</Label>
          <Input
            type="number" step="0.01"
            value={physicalCash}
            onChange={(e) => setPhysicalCash(e.target.value)}
            className="rounded-none font-mono-num h-11 text-lg"
            placeholder="e.g. 8500"
            data-testid="physical-cash-panel-input"
          />
          {variance !== null && (
            <div className={`mt-2 border-2 p-3 ${parseFloat(variance) === 0 ? "border-emerald-600 bg-emerald-50" : "border-[#FF2400] bg-red-50"}`}>
              <div className="overline text-slate-500">Cash variance</div>
              <div
                className={`font-mono-num font-bold text-xl mt-1 ${parseFloat(variance) === 0 ? "text-emerald-700" : "text-[#FF2400]"}`}
                data-testid="cash-variance-panel"
              >
                {variance > 0 ? "+" : ""}₹{variance}
              </div>
            </div>
          )}
        </div>
        <div>
          <Label className="overline">Amount Received on GPay</Label>
          <Input
            type="number" step="0.01"
            value={physicalOnline}
            onChange={(e) => setPhysicalOnline(e.target.value)}
            className="rounded-none font-mono-num h-11 text-lg"
            placeholder={`e.g. ${expectedOnline}`}
            data-testid="physical-online-panel-input"
          />
          {onlineVariance !== null && (
            <div className={`mt-2 border-2 p-3 ${parseFloat(onlineVariance) === 0 ? "border-emerald-600 bg-emerald-50" : "border-[#FF2400] bg-red-50"}`}>
              <div className="overline text-slate-500">Online variance</div>
              <div
                className={`font-mono-num font-bold text-xl mt-1 ${parseFloat(onlineVariance) === 0 ? "text-emerald-700" : "text-[#FF2400]"}`}
                data-testid="online-variance-panel"
              >
                {onlineVariance > 0 ? "+" : ""}₹{onlineVariance}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
          @page { size: A4 portrait; margin: 10mm 15mm; }
          /* 16:9 landscape slip printed on A4 portrait paper */
          .print-area {
            border: 1px solid #000 !important;
            padding: 4mm !important;
            width: 180mm !important;
            max-width: 180mm !important;
            height: 101mm !important;  /* 180 × 9/16 = 101.25 → 16:9 ratio */
            box-sizing: border-box !important;
            overflow: hidden !important;
            page-break-inside: avoid !important;
            margin: 0 auto !important;
          }
          .print-area, .print-area * { font-size: 8pt !important; line-height: 1.2 !important; }
          .print-area .font-display { font-size: 11pt !important; }
          .print-area .overline { font-size: 6.5pt !important; letter-spacing: 0.1em !important; }
          .print-area .text-lg { font-size: 10pt !important; }
          .print-area .space-y-1 > * + * { margin-top: 0 !important; }
          .print-area .mb-3, .print-area .mb-5 { margin-bottom: 2mm !important; }
          .print-area input { border: 0 !important; border-bottom: 1px solid #000 !important; background: transparent !important; color: #000 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-5">
      <div className="overline text-slate-500 border-b border-slate-200 pb-1 mb-2">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value, plus, minus, bold, big, subtle, accent }) {
  return (
    <div className={`flex items-center justify-between py-1.5 ${bold ? "font-semibold" : ""}`}>
      <div className={`text-sm ${subtle ? "text-slate-400" : "text-slate-700"}`}>
        {plus && <span className="text-emerald-600 mr-1">+</span>}
        {minus && <span className="text-[#FF2400] mr-1">−</span>}
        {label}
      </div>
      <div className={`font-mono-num ${big ? "text-lg" : "text-sm"} ${accent ? "text-[#FF2400]" : ""}`}>{value}</div>
    </div>
  );
}

function PhysicalRow({ label, value, onChange, testId, placeholder = "" }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <div className="text-sm text-slate-600">{label}</div>
      <input
        type="number"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="font-mono-num text-sm text-right border-b border-slate-400 focus:border-brand-orange outline-none bg-transparent w-24 print:border-b print:border-slate-700"
      />
    </div>
  );
}
